import { MERCURY_TRIAL_TENANT } from '../features/mercuryCustomerTrial';
import {
  createAuth0User, getTenantById, updateTenant, sendPasswordReset,
  getCarrierOnboardingDirectory, inviteCarrierPlatformUser, resetCarrierPlatformPassword,
} from "../api/tenantApi";
import { matchCarrierCompany } from "./carrierInsights";

// Auth0 password policy: >=8 chars incl. upper, lower, digit, special (!@#$%^&*).
// The prospect never sees this — they set their own via the activation email.
export const randomPassword = () => {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digit = "23456789";
  const special = "!@#$%^&*";
  const all = upper + lower + digit + special;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let pw = pick(upper) + pick(lower) + pick(digit) + pick(special);
  for (let i = 0; i < 14; i++) pw += pick(all);
  return pw.split("").sort(() => Math.random() - 0.5).join("");
};

// The original invitation flow needs an internal initial credential. Mercury
// uses browser cryptography; its value is never displayed or emailed.
const mercuryTemporaryPassword = () => {
  if (typeof crypto?.getRandomValues !== 'function') throw new Error('Secure invitation preparation is unavailable');
  const groups = ['ABCDEFGHJKLMNPQRSTUVWXYZ', 'abcdefghijkmnpqrstuvwxyz', '23456789', '!@#$%^&*'];
  const values = crypto.getRandomValues(new Uint32Array(18));
  const all = groups.join('');
  return [...groups.map((group, index) => group[values[index] % group.length]),
    ...Array.from(values.slice(4), value => all[value % all.length])].join('');
};

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Invite a single user into a tenant:
 *  1. create the Auth0 login account (random password),
 *  2. add them to the tenant's email list,
 *  3. send an activation / set-password email.
 * Returns nothing; throws on the account-creation step (the important one).
 */
export const resolveInvitePlatform = async (tenant, carrierCompany = null) => {
  if (carrierCompany?.id) return { kind: "carrier", company: carrierCompany };
  const configuredId = tenant?.metadata?.carrierPlatformCompanyId || tenant?.carrierPlatformCompanyId;
  let directory;
  try {
    directory = await getCarrierOnboardingDirectory();
  } catch (error) {
    // The carrier directory is a CrediArc-admin endpoint, but here it is only a
    // routing hint. A tenant admin inviting into a plain risk tenant must not
    // fail on it; a tenant explicitly linked to the carrier platform still
    // surfaces the failure rather than silently mis-routing the invite.
    if (error?.response?.status === 403 && !configuredId) return { kind: "risk" };
    if (configuredId) throw new Error("Carrier platform is temporarily unavailable; no invite was sent");
    throw error;
  }
  const company = matchCarrierCompany(tenant, directory);
  return company ? { kind: "carrier", company } : { kind: "risk" };
};

// isTenantAdmin marks the invitee as an admin of this tenant only (may manage
// users within the org). isAdmin remains the global CrediArc-staff flag and is
// never set by the tenant-facing invite dialog; the backend also refuses it
// from tenant admins.
export const inviteUser = async (tenant, { email, name, roleId = null, isAdmin = false, isTenantAdmin = false, carrierCompany = null }) => {
  const tenantId = tenant?.id;
  if (!tenantId) throw new Error("Missing tenant");
  const clean = String(email || "").trim();
  if (!EMAIL_RE.test(clean)) throw new Error("Invalid email");

  if (tenantId === MERCURY_TRIAL_TENANT) {
    const approved = ['hadar.segal@mercurytc.com', 'olga.tzioutziou@mercurytc.com', 'youssef.samia@mercurytc.com'];
    const recipient = clean.toLowerCase();
    if (!approved.includes(recipient) || isAdmin || isTenantAdmin || roleId) throw new Error('Mercury invitations require the approved ordinary Member scope');
  }

  const platform = tenantId === MERCURY_TRIAL_TENANT ? { kind: 'risk' } : await resolveInvitePlatform(tenant, carrierCompany);
  if (platform.kind === "carrier") {
    await inviteCarrierPlatformUser({
      companyId: platform.company.id,
      email: clean,
      fullName: name?.trim() || clean.split("@")[0],
      role: isAdmin || isTenantAdmin ? "company_admin" : "viewer",
    });
    return { platform: "carrier", companyId: platform.company.id };
  }

  // 1. Create the account. If it already exists, that's fine — we'll just
  //    (re)send the activation email below, so an invite is idempotent.
  try {
    await createAuth0User({
      email: clean,
      name: name?.trim() || clean.split("@")[0],
      password: tenantId === MERCURY_TRIAL_TENANT ? mercuryTemporaryPassword() : randomPassword(),
      connection: "Username-Password-Authentication",
      user_metadata: { tenantId, roleId: roleId || null, isAdmin: !!isAdmin, isTenantAdmin: !!isTenantAdmin },
      app_metadata: { tenantId, roleId: roleId || null },
      verify_email: false,
    });
  } catch (e) {
    // Mercury backend reconciles known identities and returns success itself.
    // A collision or uncertain outcome must never fall through to an email.
    if (tenantId === MERCURY_TRIAL_TENANT) throw e;
    const detail = e?.response?.data?.details || e?.response?.data || {};
    const msg = `${detail.message || detail.error || e?.message || ""}`.toLowerCase();
    const status = detail.statusCode || e?.response?.status;
    const alreadyExists = status === 409 || msg.includes("already exists");
    if (!alreadyExists) throw e; // genuine failure
    // else fall through — account exists, just resend the activation email.
  }

  // 2. Keep the tenant's email list in sync (non-fatal).
  try {
    const tRes = await getTenantById(tenantId);
    const tData = tRes?.data || {};
    if (!tData?.emailList?.includes(clean)) {
      await updateTenant(tenantId, { emailList: tData.emailList ? [...tData.emailList, clean] : [clean] });
    }
  } catch (e) {
    console.warn("emailList sync failed for", clean, e?.message);
  }

  // 3. Send the activation e-mail (branded, via Resend) so they set their password.
  const delivery = await sendPasswordReset(clean, { name: name?.trim(), tenantName: tenant?.name,
    ...(tenantId === MERCURY_TRIAL_TENANT && { tenantId }) });
  return { platform: "risk", ...(tenantId === MERCURY_TRIAL_TENANT && delivery?.alreadySent && { alreadySent: true }) };
};

export const sendTenantPasswordReset = async (tenant, email, { name, carrierCompany = null } = {}) => {
  const clean = String(email || "").trim();
  if (!EMAIL_RE.test(clean)) throw new Error("Invalid email");
  const platform = await resolveInvitePlatform(tenant, carrierCompany);
  if (platform.kind === "carrier") {
    await resetCarrierPlatformPassword(platform.company.id, clean);
    return { platform: "carrier", companyId: platform.company.id };
  }
  await sendPasswordReset(clean, { name, tenantName: tenant?.name,
    ...(tenant?.id === MERCURY_TRIAL_TENANT && { tenantId: tenant.id }) });
  return { platform: "risk" };
};

// Parse a pasted block of emails (commas / spaces / newlines), de-duplicated.
export const parseEmails = (text) => {
  const parts = String(text || "").split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
  return [...new Set(parts)];
};
